import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ExpandMore, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { normalizePartNumber } from '../../utils/partNumber';
import { getWorkOrderDisplayNumber, getWorkOrderCandidateNumber } from '../../utils/workOrderDisplayNumber';
import { useWorkOrderDisplayContext } from '../maintenance/hooks/useWorkOrderDisplayContext';

const ITEM_TYPE_CONFIG = {
  SPARE: { label: 'Spare', categories: ['PART'] },
  CONSUMABLE: { label: 'Consumable', categories: ['CONSUMABLE', 'MATERIAL'] },
  TOOL: { label: 'Tool / Equipment', categories: ['TOOL', 'GSE'] }
};

const PO_RAISED_STATUSES = new Set([
  'PENDING_STORE_APPROVAL',
  'PENDING_AM_APPROVAL',
  'ISSUED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CLOSED'
]);

const ACTIVE_PO_STATUSES = new Set([
  'PENDING_STORE_APPROVAL',
  'PENDING_AM_APPROVAL',
  'ISSUED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CLOSED'
]);

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function sumInventoryQuantity(rows) {
  return rows.reduce((total, row) => total + (Number(row?.quantity) || 0), 0);
}

function buildInventoryIndexes(items) {
  const byCategoryAndPart = new Map();
  const byCategoryAndReference = new Map();

  items.forEach((item) => {
    if (item?.isArchived || item?.status !== 'IN_INVENTORY') return;
    const category = String(item.category || '').toUpperCase();
    const pn = normalizePartNumber(item.partNumber);
    const refPn = normalizePartNumber(item.referencePartNumber);

    if (pn) {
      const key = `${category}::${pn}`;
      const existing = byCategoryAndPart.get(key) || [];
      existing.push(item);
      byCategoryAndPart.set(key, existing);
    }

    if (refPn) {
      const key = `${category}::${refPn}`;
      const existing = byCategoryAndReference.get(key) || [];
      existing.push(item);
      byCategoryAndReference.set(key, existing);
    }
  });

  return { byCategoryAndPart, byCategoryAndReference };
}

function buildPoIndex(purchaseOrders) {
  const byCategoryAndPart = new Map();

  purchaseOrders.forEach((po) => {
    if (!ACTIVE_PO_STATUSES.has(po?.status) || po?.isArchived) return;
    (po.lineItems || []).forEach((line) => {
      const pn = normalizePartNumber(line?.partNumber);
      if (!pn) return;
      const category = String(line?.category || 'PART').toUpperCase();
      const key = `${category}::${pn}`;
      const existing = byCategoryAndPart.get(key) || [];
      existing.push({
        poId: po._id,
        poNumber: po.poNumber,
        poStatus: po.status,
        quantity: line?.lineStatus === 'CLOSED' ? 0 : (Number(line.quantity) || 0),
        receivedQuantity: Number(line.receivedQuantity) || 0,
        outstandingQuantity: line?.lineStatus === 'CLOSED'
          ? 0
          : Math.max(0, (Number(line.quantity) || 0) - (Number(line.receivedQuantity) || 0)),
        lineStatus: line.lineStatus || 'OPEN'
      });
      byCategoryAndPart.set(key, existing);
    });
  });

  return byCategoryAndPart;
}

function buildRequirementLines(wo) {
  const spares = (wo.requiredSpares || []).map((item) => ({
    id: item._id || `spare-${item.sequenceNumber}-${item.partNumber}`,
    type: 'SPARE',
    name: item.itemName || '',
    partNumber: normalizePartNumber(item.partNumber),
    referencePartNumber: normalizePartNumber(item.referencePartNumber),
    quantityRequired: Number(item.quantity) || 0,
    taskReference: item.taskReference || '',
    remarks: item.remarks || ''
  }));

  const consumables = (wo.requiredConsumables || []).map((item) => ({
    id: item._id || `consumable-${item.sequenceNumber}-${item.partNumberOrSpecification}`,
    type: 'CONSUMABLE',
    name: item.itemName || '',
    partNumber: normalizePartNumber(item.partNumberOrSpecification),
    referencePartNumber: normalizePartNumber(item.referencePartNumber),
    quantityRequired: Number(item.quantity) || 0,
    taskReference: item.taskReference || '',
    remarks: item.remarks || ''
  }));

  const tools = (wo.requiredToolsEquipment || []).map((item) => ({
    id: item._id || `tool-${item.sequenceNumber}-${item.partNumber}`,
    type: 'TOOL',
    name: item.itemName || '',
    partNumber: normalizePartNumber(item.partNumber) || String(item.partNumber || '').trim(),
    referencePartNumber: normalizePartNumber(item.referencePartNumber),
    quantityRequired: Math.max(Number(item.quantity) || 1, 1),
    taskReference: item.taskReferenceId || '',
    remarks: item.remarks || ''
  }));

  return [...spares, ...consumables, ...tools].filter(
    (line) => (line.partNumber || line.name) && line.quantityRequired > 0
  );
}

function enrichRequirementLine(line, inventoryIndex, poIndex) {
  const config = ITEM_TYPE_CONFIG[line.type] || ITEM_TYPE_CONFIG.SPARE;
  const inventoryMatches = [];

  config.categories.forEach((category) => {
    const pnKey = `${category}::${line.partNumber}`;
    inventoryMatches.push(...(inventoryIndex.byCategoryAndPart.get(pnKey) || []));

    if (line.referencePartNumber) {
      const refKey = `${category}::${line.referencePartNumber}`;
      inventoryMatches.push(...(inventoryIndex.byCategoryAndReference.get(refKey) || []));
    }
  });

  const uniqueInventoryMatches = Array.from(
    new Map(inventoryMatches.map((item) => [String(item._id), item])).values()
  );

  const availableQuantity = sumInventoryQuantity(uniqueInventoryMatches);
  const shortageQuantity = Math.max(line.quantityRequired - availableQuantity, 0);
  const poMatches = config.categories.flatMap((category) => poIndex.get(`${category}::${line.partNumber}`) || []);
  const poRaisedQuantity = poMatches.reduce((total, item) => total + (item.quantity || 0), 0);
  const poReceivedQuantity = poMatches.reduce((total, item) => total + (item.receivedQuantity || 0), 0);
  const poOutstandingQuantity = poMatches.reduce((total, item) => total + (item.outstandingQuantity || 0), 0);
  const availableStatus = shortageQuantity > 0 ? 'PARTIAL' : 'AVAILABLE';
  const poStatuses = [...new Set(poMatches.map((item) => item.poStatus).filter(Boolean))];
  const poNumbers = [...new Set(poMatches.map((item) => item.poNumber).filter(Boolean))];

  return {
    ...line,
    typeLabel: config.label,
    availableQuantity,
    shortageQuantity,
    unavailableQuantity: shortageQuantity,
    poRaisedQuantity,
    poReceivedQuantity,
    poOutstandingQuantity,
    availableStatus,
    poStatuses,
    poNumbers
  };
}

const StoreWorkOrderRequirements = () => {
  const { showToast } = useToast();
  const displayContext = useWorkOrderDisplayContext();
  const woDisplayLabel = useCallback(
    (wo) => getWorkOrderDisplayNumber(wo, displayContext) || wo?.title || wo?._id || '',
    [displayContext]
  );
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [aircraftFilter, setAircraftFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [maintenanceRes, inventoryRes, poRes] = await Promise.all([
        axios.get('/api/maintenance'),
        axios.get('/api/store/inventory'),
        axios.get('/api/store/purchase-orders?lineItemExport=true')
      ]);

      const maintenanceRows = toArray(maintenanceRes.data);
      const inventoryRows = toArray(inventoryRes.data);
      const poRows = toArray(poRes.data);
      const inventoryIndex = buildInventoryIndexes(inventoryRows);
      const poIndex = buildPoIndex(poRows);

      const prepared = maintenanceRows
        .filter((wo) => wo.status !== 'CANCELLED')
        .map((wo) => {
          const lines = buildRequirementLines(wo).map((line) =>
            enrichRequirementLine(line, inventoryIndex, poIndex)
          );

          const totalRequired = lines.reduce((sum, line) => sum + line.quantityRequired, 0);
          const totalAvailable = lines.reduce((sum, line) => sum + line.availableQuantity, 0);
          const totalUnavailable = lines.reduce((sum, line) => sum + line.unavailableQuantity, 0);
          const totalPoRaised = lines.reduce((sum, line) => sum + line.poRaisedQuantity, 0);
          const totalPoReceived = lines.reduce((sum, line) => sum + line.poReceivedQuantity, 0);
          const totalPoOutstanding = lines.reduce((sum, line) => sum + line.poOutstandingQuantity, 0);

          return {
            ...wo,
            requirementLines: lines,
            totalRequired,
            totalAvailable,
            totalUnavailable,
            totalPoRaised,
            totalPoReceived,
            totalPoOutstanding
          };
        })
        .filter((wo) => wo.requirementLines.length > 0)
        .sort((a, b) => {
          const aDate = new Date(a.scheduledDate || a.updatedAt || a.createdAt || 0).getTime();
          const bDate = new Date(b.scheduledDate || b.updatedAt || b.createdAt || 0).getTime();
          return bDate - aDate;
        });

      setWorkOrders(prepared);
    } catch (error) {
      console.error(error);
      showToast('Could not load work order requirements.', 'error');
      setWorkOrders([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const aircraftOptions = useMemo(() => {
    const labels = [...new Set(workOrders.map((wo) => wo.aircraftId?.registration || wo.aircraftRegistration).filter(Boolean))];
    return labels.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [workOrders]);

  const filteredWorkOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return workOrders.filter((wo) => {
      const aircraft = wo.aircraftId?.registration || wo.aircraftRegistration || '';
      const matchesAircraft = aircraftFilter === 'ALL' || aircraft === aircraftFilter;
      const matchesStatus = statusFilter === 'ALL' || wo.status === statusFilter;
      const matchesSearch =
        !term ||
        woDisplayLabel(wo).toLowerCase().includes(term) ||
        String(wo.mroJobNumber || '').toLowerCase().includes(term) ||
        String(wo.workOrderNumber || '').toLowerCase().includes(term) ||
        String(wo.title || '').toLowerCase().includes(term) ||
        aircraft.toLowerCase().includes(term) ||
        wo.requirementLines.some((line) =>
          [line.partNumber, line.referencePartNumber, line.name, line.remarks, line.taskReference]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term))
        );

      return matchesAircraft && matchesStatus && matchesSearch;
    });
  }, [aircraftFilter, search, statusFilter, workOrders, woDisplayLabel]);

  if (loading) {
    return <Typography>Loading work order requirements...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Work Order Requirements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View every work order material and tool requirement with live stock availability and purchase order progress.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchData} aria-label="Refresh requirements">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Search WO / part / item"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="wo-req-aircraft-filter-label">Aircraft</InputLabel>
            <Select
              labelId="wo-req-aircraft-filter-label"
              value={aircraftFilter}
              label="Aircraft"
              onChange={(event) => setAircraftFilter(event.target.value)}
            >
              <MenuItem value="ALL">All Aircraft</MenuItem>
              {aircraftOptions.map((aircraft) => (
                <MenuItem key={aircraft} value={aircraft}>
                  {aircraft}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="wo-req-status-filter-label">WO Status</InputLabel>
            <Select
              labelId="wo-req-status-filter-label"
              value={statusFilter}
              label="WO Status"
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              {[...new Set(workOrders.map((wo) => wo.status).filter(Boolean))].map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {filteredWorkOrders.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No work order requirements match the selected filters.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredWorkOrders.map((wo) => {
            const aircraft = wo.aircraftId?.registration || wo.aircraftRegistration || '—';
            return (
              <Accordion key={wo._id} disableGutters>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', lg: 'center' }}
                    sx={{ width: '100%', pr: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {woDisplayLabel(wo)} - {wo.title}
                        {getWorkOrderCandidateNumber(wo, displayContext) ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                            label={displayContext === 'mro' ? `WO: ${getWorkOrderCandidateNumber(wo, displayContext)}` : `MRO: ${getWorkOrderCandidateNumber(wo, displayContext)}`}
                          />
                        ) : null}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Aircraft: {aircraft} | WO status: {wo.status} | Scheduled:{' '}
                        {wo.scheduledDate ? new Date(wo.scheduledDate).toLocaleDateString() : '—'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`Required ${wo.totalRequired}`} />
                      <Chip size="small" color="success" label={`Available ${wo.totalAvailable}`} />
                      <Chip size="small" color="warning" label={`Not Available ${wo.totalUnavailable}`} />
                      <Chip size="small" color="info" label={`PO Raised ${wo.totalPoRaised}`} />
                      <Chip size="small" color="secondary" label={`Received ${wo.totalPoReceived}`} />
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>Part / Spec</TableCell>
                          <TableCell>Ref Part</TableCell>
                          <TableCell align="right">Required</TableCell>
                          <TableCell align="right">Available</TableCell>
                          <TableCell align="right">Not Available</TableCell>
                          <TableCell align="right">PO Raised</TableCell>
                          <TableCell align="right">Received</TableCell>
                          <TableCell>PO Status</TableCell>
                          <TableCell>PO No.</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {wo.requirementLines.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell>{line.typeLabel}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{line.name || '—'}</Typography>
                              {(line.taskReference || line.remarks) && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                  {[line.taskReference ? `Task: ${line.taskReference}` : '', line.remarks || '']
                                    .filter(Boolean)
                                    .join(' | ')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{line.partNumber}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {line.referencePartNumber || '—'}
                            </TableCell>
                            <TableCell align="right">{line.quantityRequired}</TableCell>
                            <TableCell align="right">{line.availableQuantity}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                color={line.unavailableQuantity > 0 ? 'warning.main' : 'text.primary'}
                              >
                                {line.unavailableQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{line.poRaisedQuantity}</TableCell>
                            <TableCell align="right">{line.poReceivedQuantity}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {line.poStatuses.length > 0 ? (
                                  line.poStatuses.map((status) => (
                                    <Chip
                                      key={`${line.id}-${status}`}
                                      size="small"
                                      label={status}
                                      color={PO_RAISED_STATUSES.has(status) ? 'info' : 'default'}
                                    />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No PO
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {line.poNumbers.length > 0 ? line.poNumbers.join(', ') : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default StoreWorkOrderRequirements;
